import React from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { FontWeightEnum } from '@app/common';
import { NoData } from '../../../no-data/no-data';
import { SkeletonAllocationsRankingsTable } from '../../../skeleton/dashboard/trades/allocations/rankings/skeleton-allocations-rankings-table';
import { ValueAmount } from '../../../value/amount/value-amount';
import { ValuePercents } from '../../../value/percents/value-precents';
import { ValueQuantity } from '../../../value/quantity/value-quantity';
import { Table } from '../../../table/table';
import { TableBody } from '../../../table/body/table-body';
import { TableRow } from '../../../table/row/table-row';
import { TableCell } from '../../../table/cell/table-cell';
import { Symbol } from '../../../symbol/symbol';
import { Text } from '../../../text/text';
import { routes } from '../../../../../config/routes';
import { SymbolTypeEnum } from '../../../../../defs/api';

export interface GainLossRowData {
    symbolId: string;
    symbol: string;
    exchangeId: string;
    type: SymbolTypeEnum;
    symbolName: string;
    symbolCurrency: string;
    amount: number;
    profitValue: number;
    feesValue?: number;
    profitPercentage: number;
    currency: string;
}

interface GainLossTableRendererProps {
    loading: boolean;
    isRecalculating: boolean;
    gainLossData: GainLossRowData[];
}

export const GainLossTableRenderer: React.FC<GainLossTableRendererProps> = observer(
    ({ loading, isRecalculating, gainLossData }) => {
        const { t } = useTranslation();

        if (loading || isRecalculating) {
            return <SkeletonAllocationsRankingsTable />;
        }

        if (gainLossData.length === 0) {
            return <NoData>{t('layout.tables.noData')}</NoData>;
        }

        return (
            <Table mobileBreakUnder="sm" containerMarginBottom="none">
                <TableBody>
                    {gainLossData.map((row) => {
                        const isCrypto = row.type === SymbolTypeEnum.Ct;
                        const displayName = isCrypto ? `${row.symbolName} (${row.symbolCurrency})` : row.symbolName;
                        const unitKey = isCrypto
                            ? 'layout.tables.trades.units.crypto'
                            : 'layout.tables.trades.units.stocks';

                        return (
                            <TableRow key={row.symbolId}>
                                <TableCell>
                                    <Symbol
                                        symbol={row.symbol}
                                        url={`${routes.app.symbols}/${row.exchangeId}/${row.symbol}`}
                                        name={displayName}
                                        type={row.type}
                                        description={
                                            <ValueQuantity
                                                amount={row.amount}
                                                suffix={` ${t(unitKey)}`}
                                                fractionDigits={isCrypto ? 8 : 2}
                                                inParentheses
                                                mode="pure"
                                            />
                                        }
                                        ticker={row.symbol}
                                    />
                                </TableCell>
                                <TableCell
                                    label={t('layout.tables.performanceOfIndividualAssets.profit')}
                                    align="right"
                                >
                                    <Text semiBold>
                                        <ValueAmount
                                            hidable
                                            amount={row.profitValue}
                                            currency={row.currency}
                                            fontWeight={FontWeightEnum.SemiBold}
                                            nullValue={0}
                                        />
                                    </Text>
                                    <Text size="small">
                                        <ValuePercents amount={row.profitPercentage} inParentheses />
                                    </Text>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        );
    },
);
